/** leads/LeadFpSettingsModal.tsx — FP별 장바구니 한도 설정 (프리셋 + 직접입력) */
import React, { useState } from 'react';
import { setCartSize, rotateFpPortalLink } from './api';
import type { MetaFp } from './types';

interface Props {
  fps: MetaFp[];
  onClose: () => void;
  onChanged: () => void;
  onToast: (m: string, t?: string) => void;
}

const PRESETS = [5, 10, 15, 20, 30, 50, 100];

export default function LeadFpSettingsModal({ fps, onClose, onChanged, onToast }: Props) {
  const [busyId, setBusyId] = useState('');
  const [customId, setCustomId] = useState('');
  const [customVal, setCustomVal] = useState('');

  const rotate = async (fp: MetaFp) => {
    if (!window.confirm(fp.name + ' FP의 포털 링크를 재발급할까요?\n\n기존 링크는 즉시 사용 불가가 되며(유출 대응), 새 링크를 FP에게 다시 보내야 합니다.')) return;
    setBusyId(fp.id);
    try {
      const r = await rotateFpPortalLink(fp.id);
      const url = r.short_url || r.full_url || r.url; // url = 절대 URL (백엔드 toPortalUrls 계약)
      if (navigator.clipboard) {
        try { await navigator.clipboard.writeText(url); onToast(fp.name + ' FP 새 링크 복사됨 — 기존 링크는 무효화됐습니다'); }
        catch (ce) { onToast('재발급 완료: ' + url); }
      } else onToast('재발급 완료: ' + url);
    } catch (e) { onToast((e as Error).message, 'error'); } finally { setBusyId(''); }
  };

  const save = async (fp: MetaFp, size: number) => {
    if (!size || size < 1 || size > 1000) { onToast('한도는 1~1000 사이여야 합니다', 'error'); return; }
    setBusyId(fp.id);
    try {
      await setCartSize(fp.id, size);
      onToast(fp.name + ' FP 한도 → ' + size + '건');
      setCustomId(''); setCustomVal('');
      onChanged();
    } catch (e) { onToast((e as Error).message, 'error'); } finally { setBusyId(''); }
  };

  return (
    <div className="ld-modal-bg ld-root" onClick={onClose}>
      <div className="ld-modal" style={{ width: 'min(720px, 100%)' }} onClick={(e) => e.stopPropagation()}>
        <div className="ld-modal-h">
          <span>FP 장바구니 한도 설정</span>
          <button className="ld-btn" onClick={onClose}>닫기</button>
        </div>
        <div className="ld-modal-b">
          <div style={{ fontSize: 12, color: '#4B5563', marginBottom: 10 }}>
            한도 = FP가 동시에 보유할 수 있는 DB 수(배정~진행·보류). 성공/실패 종결 또는 반납 시 자동으로 비워집니다.
            한도를 현재 보유보다 줄여도 강제 회수되지 않으며, 보유가 한도 아래로 내려갈 때까지 신규 수령만 차단됩니다.
          </div>
          <table className="ld-table">
            <thead><tr><th>FP</th><th>지점</th><th>보유/한도</th><th>한도 변경</th><th>포털 링크</th></tr></thead>
            <tbody>
              {fps.map((f) => {
                const full = f.active_count >= f.cart_size;
                return (
                  <tr key={f.id} style={{ cursor: 'default' }}>
                    <td><b>{f.name}</b></td>
                    <td>{f.company || '-'}</td>
                    <td>
                      <span className="ld-year" style={full ? { background: '#FEF2F2', color: '#DC2626' } : {}}>
                        {f.active_count} / {f.cart_size}{full ? ' 가득참' : ''}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        <select disabled={busyId === f.id} value=""
                          onChange={(e) => { const v = e.target.value; if (v === 'custom') { setCustomId(f.id); setCustomVal(String(f.cart_size)); } else if (v) save(f, Number(v)); }}
                          style={{ padding: '6px 8px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 12 }}>
                          <option value="">선택…</option>
                          {PRESETS.map((p) => <option key={p} value={p}>{p}건{p === f.cart_size ? ' (현재)' : ''}</option>)}
                          <option value="custom">직접 입력</option>
                        </select>
                        {customId === f.id && (
                          <>
                            <input value={customVal} onChange={(e) => setCustomVal(e.target.value.replace(/[^0-9]/g, ''))}
                              style={{ width: 64, padding: '6px 8px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 12 }} />
                            <button className="ld-btn pri" disabled={busyId === f.id} onClick={() => save(f, Number(customVal))}>저장</button>
                          </>
                        )}
                      </div>
                    </td>
                    <td>
                      <button className="ld-btn" style={{ padding: '5px 10px', fontSize: 12, color: '#EA580C', borderColor: '#FED7AA' }}
                        disabled={busyId === f.id} onClick={() => rotate(f)}
                        title="유출 시 재발급 — 기존 링크 즉시 무효">링크 재발급</button>
                    </td>
                  </tr>
                );
              })}
              {fps.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: '#9CA3AF', padding: 20 }}>등록된 FP가 없습니다</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
